import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { useClickOutside } from '@/utils/useClickOutside';
import { X } from 'lucide-react';
import { useRef, type FC } from 'react';
import type { AddItemsMode } from '../AddItemsSidebar';
import { DatabasePanel } from './DatabasePanel';
import { FilesPanel } from './FilesPanel';

interface AddItemsPanelProps {
  mode: AddItemsMode;
  onClose: () => void;
  tableViewId: string;
}

export const AddItemsPanel: FC<AddItemsPanelProps> = ({
  mode,
  onClose,
  tableViewId,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close panel when clicking outside, but ignore clicks on the sidebar, dropdown portals, and dialog modals
  useClickOutside(panelRef, onClose, {
    ignoreSelector:
      '[data-add-items-sidebar], [data-radix-popper-content-wrapper], [role="dialog"], [data-state="open"][role="dialog"]',
    enabled: mode !== null,
  });

  if (!mode) return null;

  const title = mode === 'database' ? 'Databases' : 'Documents';
  const subtitle = 'Row Items';

  return (
    <FlexCol
      ref={panelRef}
      className="absolute top-0 right-[53px] bottom-0 w-[600px] bg-tan-100 overflow-hidden animate-slide-in-from-sidebar z-10"
      style={{
        boxShadow:
          '-8px 0 24px -4px rgba(0, 0, 0, 0.1), -4px 0 8px -2px rgba(0, 0, 0, 0.05)',
      }}
    >
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 p-6 shrink-0">
        <FlexRow justifyContent="between" alignItems="start">
          <FlexCol gap="3">
            <Text
              variant="body-xs"
              className="font-medium text-neutral-600 uppercase tracking-wider"
            >
              {subtitle}
            </Text>
            <Text className="font-semibold text-xl">{title}</Text>
          </FlexCol>
          <Button
            variant="outline"
            size="icon"
            onClick={onClose}
            className="h-10 w-10"
          >
            <X className="w-4 h-4" />
          </Button>
        </FlexRow>
      </div>

      {/* Content */}
      {mode === 'database' ? (
        <DatabasePanel tableViewId={tableViewId} />
      ) : (
        <FilesPanel tableViewId={tableViewId} />
      )}
    </FlexCol>
  );
};
